import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useCrawlOverview, useLighthouseData, useAiAuditSummary, useAiAnalysisSummary, type CrawlOverview } from '../hooks/useCrawls'
import { scoreColor } from '@/client/lib/colors'
import { useSearchParams } from 'react-router-dom'

/** Reusable horizontal bar row — optionally clickable for filter navigation */
function BarRow({
  label,
  value,
  total,
  color,
  onClick,
}: {
  label: string
  value: number
  total: number
  color: string
  onClick?: () => void
}) {
  const pct = total > 0 ? (value / total) * 100 : 0
  const Wrapper = onClick && value > 0 ? 'button' : 'div'
  return (
    <Wrapper
      className={`flex items-center gap-3 text-sm w-full${onClick && value > 0 ? ' cursor-pointer rounded-sm hover:bg-muted/60 -mx-1 px-1 transition-colors' : ''}`}
      onClick={onClick && value > 0 ? onClick : undefined}
      title={onClick && value > 0 ? `Click to filter: ${label}` : label}
    >
      <span className="w-24 shrink-0 text-muted-foreground truncate">
        {label}
      </span>
      <div className="flex-1 h-5 rounded-sm bg-muted overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${value} of ${total}`}>
        {pct > 0 && (
          <div
            className={`h-full rounded-sm transition-all ${color}`}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        )}
      </div>
      <span className="w-12 text-right tabular-nums font-medium">{value}</span>
    </Wrapper>
  )
}

/** SEO readiness percentage row */
function ReadinessRow({
  label,
  count,
  total,
}: {
  label: string
  count: number
  total: number
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <div className="flex-1 h-5 rounded-sm bg-muted overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${pct}%`}>
        <div
          className="h-full rounded-sm bg-green-500/80 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-12 text-right tabular-nums font-medium">{pct}%</span>
    </div>
  )
}

function StatusCodesCard({ data, onFilter }: { data: CrawlOverview; onFilter: (filter: string) => void }) {
  const { statusCodes, totalPages } = data
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Status Codes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <BarRow label="2xx Success" value={statusCodes['2xx']} total={totalPages} color="bg-green-500/80" onClick={() => onFilter('status_2xx')} />
        <BarRow label="3xx Redirect" value={statusCodes['3xx']} total={totalPages} color="bg-blue-500/80" onClick={() => onFilter('status_3xx')} />
        <BarRow label="4xx Client" value={statusCodes['4xx']} total={totalPages} color="bg-yellow-500/80" onClick={() => onFilter('status_4xx')} />
        <BarRow label="5xx Server" value={statusCodes['5xx']} total={totalPages} color="bg-red-500/80" onClick={() => onFilter('status_5xx')} />
      </CardContent>
    </Card>
  )
}

function ResponseTimeCard({ data, onFilter }: { data: CrawlOverview; onFilter: (filter: string) => void }) {
  const { responseTime, totalPages } = data
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Response Time</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <BarRow label="≤200ms" value={responseTime.fast} total={totalPages} color="bg-green-500/80" />
        <BarRow label="200–500ms" value={responseTime.ok} total={totalPages} color="bg-emerald-400/80" />
        <BarRow label="500ms–1s" value={responseTime.slow} total={totalPages} color="bg-yellow-500/80" />
        <BarRow label="1–2s" value={responseTime.verySlow} total={totalPages} color="bg-orange-500/80" />
        <BarRow label=">2s" value={responseTime.critical} total={totalPages} color="bg-red-500/80" onClick={() => onFilter('slow_response')} />
      </CardContent>
    </Card>
  )
}

function ContentLengthCard({ data, onFilter }: { data: CrawlOverview; onFilter: (filter: string) => void }) {
  const { contentLength } = data
  const total = contentLength.empty + contentLength.thin + contentLength.short + contentLength.medium + contentLength.long
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Content Length</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <BarRow label="1000+ words" value={contentLength.long} total={total} color="bg-green-500/80" onClick={() => onFilter('content_long')} />
        <BarRow label="500–999" value={contentLength.medium} total={total} color="bg-emerald-400/80" onClick={() => onFilter('content_medium')} />
        <BarRow label="200–499" value={contentLength.short} total={total} color="bg-yellow-500/80" onClick={() => onFilter('content_short')} />
        <BarRow label="<200" value={contentLength.thin} total={total} color="bg-orange-500/80" onClick={() => onFilter('content_thin')} />
        <BarRow label="Empty" value={contentLength.empty} total={total} color="bg-red-500/80" onClick={() => onFilter('content_empty')} />
      </CardContent>
    </Card>
  )
}

function SeoReadinessCard({ data }: { data: CrawlOverview; onFilter?: (filter: string) => void }) {
  const { seoReadiness } = data
  const total = seoReadiness.total200
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">SEO Readiness</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <ReadinessRow label="Canonical" count={seoReadiness.canonical} total={total} />
        <ReadinessRow label="Schema" count={seoReadiness.schema} total={total} />
        <ReadinessRow label="OG Tags" count={seoReadiness.ogTags} total={total} />
        <ReadinessRow label="Viewport" count={seoReadiness.viewport} total={total} />
        {data.indexability && <ReadinessRow label="Indexable" count={data.indexability.indexable} total={data.indexability.total200} />}
      </CardContent>
    </Card>
  )
}

function SeoScoreCard({ data }: { data: CrawlOverview }) {
  const { seoScore } = data
  const total = seoScore.good + seoScore.ok + seoScore.poor
  if (total === 0) return null
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          SEO Score
          {seoScore.avg > 0 && (
            <span className={`ml-2 text-base font-bold ${scoreColor(seoScore.avg)}`}>
              avg {seoScore.avg}/100
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <BarRow label="Good (80+)" value={seoScore.good} total={total} color="bg-green-500/80" />
        <BarRow label="OK (50-79)" value={seoScore.ok} total={total} color="bg-yellow-500/80" />
        <BarRow label="Poor (<50)" value={seoScore.poor} total={total} color="bg-red-500/80" />
      </CardContent>
    </Card>
  )
}

function ReadabilityCard({ data }: { data: CrawlOverview }) {
  const { readability } = data
  const total = readability.easy + readability.moderate + readability.difficult + readability.unscored
  if (total === 0) return null
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Readability
          {readability.avg > 0 && (
            <span className={`ml-2 text-base font-bold ${scoreColor(readability.avg, { good: 60, ok: 30 })}`}>
              avg {readability.avg}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <BarRow label="Easy (60+)" value={readability.easy} total={total} color="bg-green-500/80" />
        <BarRow label="Moderate" value={readability.moderate} total={total} color="bg-yellow-500/80" />
        <BarRow label="Difficult" value={readability.difficult} total={total} color="bg-red-500/80" />
        <BarRow label="Unscored" value={readability.unscored} total={total} color="bg-muted-foreground/40" />
      </CardContent>
    </Card>
  )
}

function CrawlDepthCard({ data, onFilter }: { data: CrawlOverview; onFilter: (filter: string) => void }) {
  const { depth } = data
  const total = depth.level0 + depth.level1to2 + depth.level3to4 + depth.level5plus
  if (total === 0) return null
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Crawl Depth</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <BarRow label="Homepage" value={depth.level0} total={total} color="bg-green-500/80" onClick={() => onFilter('depth_0')} />
        <BarRow label="1-2 clicks" value={depth.level1to2} total={total} color="bg-emerald-400/80" />
        <BarRow label="3-4 clicks" value={depth.level3to4} total={total} color="bg-yellow-500/80" />
        <BarRow label="5+ clicks" value={depth.level5plus} total={total} color="bg-red-500/80" onClick={() => onFilter('depth_4plus')} />
      </CardContent>
    </Card>
  )
}

function SecurityHeadersCard({ data }: { data: CrawlOverview }) {
  const { security } = data
  if (security.total200 === 0) return null
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Security Headers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <ReadinessRow label="HSTS" count={security.hsts} total={security.total200} />
        <ReadinessRow label="CSP" count={security.csp} total={security.total200} />
        <ReadinessRow label="X-Frame-Opt" count={security.xFrameOptions} total={security.total200} />
      </CardContent>
    </Card>
  )
}

function InRankCard({ data }: { data: CrawlOverview }) {
  const { inrank } = data
  const total = inrank.high + inrank.medium + inrank.low
  if (total === 0) return null
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          InRank (Internal PageRank)
          {inrank.avg > 0 && (
            <span className={`ml-2 text-base font-bold ${scoreColor(inrank.avg, { good: 7, ok: 3 })}`}>
              avg {inrank.avg}/10
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <BarRow label="High (7+)" value={inrank.high} total={total} color="bg-green-500/80" />
        <BarRow label="Medium (3-7)" value={inrank.medium} total={total} color="bg-yellow-500/80" />
        <BarRow label="Low (<3)" value={inrank.low} total={total} color="bg-red-500/80" />
      </CardContent>
    </Card>
  )
}

function LinkSummary({ data }: { data: CrawlOverview }) {
  const { links, responseTime } = data
  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <span>
            <span className="font-semibold tabular-nums">{links.total}</span>{' '}
            <span className="text-muted-foreground">links</span>
          </span>
          <span className="text-muted-foreground">
            {links.internal} internal, {links.external} external
            {links.broken > 0 && (
              <span className="text-destructive font-medium">, {links.broken} broken</span>
            )}
          </span>
          <span className="text-muted-foreground">
            Avg response: <span className="font-medium text-foreground">{responseTime.avg}ms</span>
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

interface AiCrawlerBlock {
  bot: string
  blocked: boolean
}

function AiSearchReadinessCard({ aiCrawlerRules, hasLlmsTxt }: {
  aiCrawlerRules?: string | null
  hasLlmsTxt?: number
}) {
  const blockedBots: AiCrawlerBlock[] = aiCrawlerRules ? JSON.parse(aiCrawlerRules) : []
  const hasLlms = hasLlmsTxt === 1

  // Only show the card if there's something to report
  if (blockedBots.length === 0 && !hasLlms) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">AI Search Readiness</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">/llms.txt</span>
              <Badge variant="outline" className="text-[10px]">Not found</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">AI crawlers blocked</span>
              <Badge variant="outline" className="text-[10px] text-green-600 dark:text-green-400">None</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">AI Search Readiness</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">/llms.txt</span>
            {hasLlms ? (
              <Badge variant="outline" className="text-[10px] text-green-600 dark:text-green-400">Present</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px]">Not found</Badge>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">AI crawlers blocked</span>
            {blockedBots.length > 0 ? (
              <Badge variant="destructive" className="text-[10px]">{blockedBots.length} blocked</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-green-600 dark:text-green-400">None</Badge>
            )}
          </div>
          {blockedBots.length > 0 && (
            <div className="pt-1 flex flex-wrap gap-1">
              {blockedBots.map((b) => (
                <Badge key={b.bot} variant="secondary" className="text-[10px] font-mono">
                  {b.bot}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function CwvSummaryCard({ crawlId }: { crawlId: string }) {
  const { data } = useLighthouseData(crawlId)
  if (!data || data.collected === 0) return null

  const pages = data.pages
  const perfs = pages.map(p => p.psi_performance).filter((v): v is number => v != null).sort((a, b) => a - b)
  const lcps = pages.map(p => p.psi_lcp).filter((v): v is number => v != null).sort((a, b) => a - b)
  const clss = pages.map(p => p.psi_cls).filter((v): v is number => v != null).sort((a, b) => a - b)

  const median = (arr: number[]) => arr.length === 0 ? null : arr[Math.floor(arr.length / 2)]!

  const medPerf = median(perfs)
  const medLcp = median(lcps)
  const medCls = median(clss)

  const perfColor = medPerf == null ? '' : medPerf >= 90 ? 'text-green-600 dark:text-green-400' : medPerf >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-destructive'
  const lcpColor = medLcp == null ? '' : medLcp <= 2500 ? 'text-green-600 dark:text-green-400' : medLcp <= 4000 ? 'text-yellow-600 dark:text-yellow-400' : 'text-destructive'
  const clsColor = medCls == null ? '' : medCls <= 0.1 ? 'text-green-600 dark:text-green-400' : medCls <= 0.25 ? 'text-yellow-600 dark:text-yellow-400' : 'text-destructive'

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Core Web Vitals
          <span className="ml-2 text-xs text-muted-foreground font-normal">
            {data.collected} of {data.totalHtmlPages} pages
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className={`text-2xl font-bold tabular-nums ${perfColor}`}>
              {medPerf ?? '—'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Performance</div>
          </div>
          <div>
            <div className={`text-2xl font-bold tabular-nums ${lcpColor}`}>
              {medLcp != null ? `${(medLcp / 1000).toFixed(1)}s` : '—'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">LCP</div>
          </div>
          <div>
            <div className={`text-2xl font-bold tabular-nums ${clsColor}`}>
              {medCls != null ? medCls.toFixed(3) : '—'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">CLS</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function AiAuditSummaryCard({ crawlId }: { crawlId: string }) {
  const { data } = useAiAuditSummary(crawlId)

  if (!data || data.audited === 0) return null

  const scores = data.avgScores
  const aiScoreColor = (s: number) => scoreColor(s, { good: 8, ok: 6 })

  return (
    <Card className="md:col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          AI Page Audit
          <span className="ml-2 text-xs text-muted-foreground font-normal">
            {data.audited} pages audited
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Average scores */}
        {scores && (
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className={`text-2xl font-bold tabular-nums ${aiScoreColor(scores.first_impression)}`}>
                {scores.first_impression}
              </div>
              <div className="text-xs text-muted-foreground mt-1">First Impression</div>
            </div>
            <div>
              <div className={`text-2xl font-bold tabular-nums ${aiScoreColor(scores.content_quality)}`}>
                {scores.content_quality}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Content Quality</div>
            </div>
            <div>
              <div className={`text-2xl font-bold tabular-nums ${aiScoreColor(scores.conversion_potential)}`}>
                {scores.conversion_potential}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Conversion</div>
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {/* Trust signals */}
          {data.topTrustSignals && data.topTrustSignals.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Common Trust Signals</div>
              <div className="flex flex-wrap gap-1">
                {data.topTrustSignals.slice(0, 6).map((s) => (
                  <Badge key={s.signal} variant="secondary" className="text-[10px]">
                    {s.signal} ({s.count})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* UX issues */}
          {data.topUxIssues && data.topUxIssues.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Common UX Issues</div>
              <ul className="text-xs space-y-0.5">
                {data.topUxIssues.slice(0, 5).map((ux) => (
                  <li key={ux.issue} className="text-yellow-600 dark:text-yellow-400 truncate" title={ux.issue}>
                    ! {ux.issue} ({ux.count})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Lowest scoring pages */}
        {data.lowestPages && data.lowestPages.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">Lowest Scoring Pages</div>
            <div className="text-xs space-y-0.5">
              {data.lowestPages.map((p) => (
                <div key={p.url} className="flex items-center gap-2">
                  <span className={`font-bold tabular-nums w-8 ${aiScoreColor(p.avgScore)}`}>
                    {p.avgScore}
                  </span>
                  <span className="truncate text-muted-foreground" title={p.url}>
                    {p.title || new URL(p.url).pathname}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function AiAnalysisSummaryCard({ crawlId }: { crawlId: string }) {
  const { data } = useAiAnalysisSummary(crawlId)

  if (!data || !data.scores || data.scores.total_pages === 0) return null

  const s = data.scores
  const aiScoreColor2 = (v: number | null) =>
    v == null ? 'text-muted-foreground' : scoreColor(v, { good: 8, ok: 6 })

  return (
    <Card className="md:col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          AI Analysis
          <span className="ml-2 text-xs text-muted-foreground font-normal">
            {s.total_pages} pages analysed
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 5 average scores */}
        <div className="grid grid-cols-5 gap-2 text-center">
          {[
            { label: 'First Impression', value: s.avg_first_impression },
            { label: 'Content', value: s.avg_content_quality },
            { label: 'Conversion', value: s.avg_conversion_potential },
            { label: 'SEO', value: s.avg_seo_effectiveness },
            { label: 'Accessibility', value: s.avg_accessibility },
          ].map(({ label, value }) => (
            <div key={label}>
              <div className={`text-xl font-bold tabular-nums ${aiScoreColor2(value)}`}>
                {value ?? '-'}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        {/* Page type distribution + trust signals */}
        <div className="grid gap-4 md:grid-cols-2">
          {data.pageTypes.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Page Types</div>
              <div className="flex flex-wrap gap-1">
                {data.pageTypes.map((pt) => (
                  <Badge key={pt.page_type} variant="secondary" className="text-[10px] capitalize">
                    {(pt.page_type || 'other').replace(/_/g, ' ')} ({pt.count})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {data.trustSignals.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Common Trust Signals</div>
              <div className="flex flex-wrap gap-1">
                {data.trustSignals.slice(0, 8).map((ts) => (
                  <Badge key={ts.signal} variant="secondary" className="text-[10px]">
                    {ts.signal} ({ts.count})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Top AI-detected issues */}
        {data.topIssues.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">Top AI-Detected Issues</div>
            <div className="text-xs space-y-0.5">
              {data.topIssues.slice(0, 8).map((issue) => (
                <div key={issue.issue_type} className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[9px] px-1 py-0 uppercase shrink-0 ${
                    issue.severity === 'error' ? 'text-red-600 dark:text-red-400 border-red-600/30'
                    : issue.severity === 'warning' ? 'text-yellow-600 dark:text-yellow-400 border-yellow-600/30'
                    : 'text-blue-600 dark:text-blue-400 border-blue-600/30'
                  }`}>{issue.severity}</Badge>
                  <span className="truncate" title={issue.label}>{issue.label}</span>
                  <span className="shrink-0 text-muted-foreground tabular-nums">({issue.count})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lowest scoring pages */}
        {data.lowestPages.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">Lowest Scoring Pages</div>
            <div className="text-xs space-y-0.5">
              {data.lowestPages.slice(0, 5).map((p) => (
                <div key={p.url} className="flex items-center gap-2">
                  <span className={`font-bold tabular-nums w-8 ${aiScoreColor2(p.avg_score)}`}>
                    {p.avg_score}
                  </span>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 capitalize shrink-0">
                    {(p.page_type || '').replace(/_/g, ' ')}
                  </Badge>
                  <span className="truncate text-muted-foreground" title={p.url}>
                    {p.title || new URL(p.url).pathname}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function OverviewTab({ crawlId, aiCrawlerRules, hasLlmsTxt, hasAiAnalysis }: {
  crawlId: string
  aiCrawlerRules?: string | null
  hasLlmsTxt?: number
  hasAiAnalysis?: number
}) {
  const { data, isLoading } = useCrawlOverview(crawlId)
  const [, setSearchParams] = useSearchParams()

  const handleFilter = (filter: string) => {
    setSearchParams({ tab: 'pages', filter }, { replace: false })
  }

  if (isLoading || !data) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <Skeleton className="h-52" />
        <Skeleton className="h-52" />
        <Skeleton className="h-52" />
        <Skeleton className="h-52" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <StatusCodesCard data={data} onFilter={handleFilter} />
        <ResponseTimeCard data={data} onFilter={handleFilter} />
        <ContentLengthCard data={data} onFilter={handleFilter} />
        <SeoReadinessCard data={data} />
        <SeoScoreCard data={data} />
        <ReadabilityCard data={data} />
        <CrawlDepthCard data={data} onFilter={handleFilter} />
        <SecurityHeadersCard data={data} />
        <InRankCard data={data} />
        <CwvSummaryCard crawlId={crawlId} />
        {hasAiAnalysis
          ? <AiAnalysisSummaryCard crawlId={crawlId} />
          : <AiAuditSummaryCard crawlId={crawlId} />
        }
        <AiSearchReadinessCard aiCrawlerRules={aiCrawlerRules} hasLlmsTxt={hasLlmsTxt} />
      </div>
      <LinkSummary data={data} />
    </div>
  )
}
